/**
 * Which quality tier the game runs at, with the machine's own guess as one of the options.
 *
 * It writes to [tierPreference](../../../render/post/tierPreference.ts), which every running
 * pipeline subscribes to, so pressing a tier rebuilds the effect chain behind the screen. `auto`
 * stores the absence of an opinion rather than a copy of today's guess, which is what lets the
 * same profile land on a weaker machine and still get that machine's own answer.
 *
 * A `?tier=` on the URL outranks this knob entirely; the preference module says why.
 */

import { QUALITY_TIERS } from '@/render/post/qualityTier';
import { setPreferredTier } from '@/render/post/tierPreference';
import { choiceApply } from '@/ui/settings/controls/choiceButtons';
import {
  SETTING_QUALITY_TIER_AUTO,
  SETTING_QUALITY_TIER_HINT,
  SETTING_QUALITY_TIER_LABEL,
  SETTING_QUALITY_TIER_NAMES,
} from '@/text/settingsText';
import type { SettingDefinition } from '@/ui/settings/settingContract';

/** What goes to disk for the machine's own choice. Not a tier, and never resolved into one. */
const AUTO = 'auto';

export const QUALITY_TIER_SETTING: SettingDefinition = {
  id: 'qualityTier',
  section: 'video',
  label: SETTING_QUALITY_TIER_LABEL,
  hint: SETTING_QUALITY_TIER_HINT,
  control: {
    kind: 'choice',
    options: [
      { value: AUTO, label: SETTING_QUALITY_TIER_AUTO },
      ...QUALITY_TIERS.map((tier) => ({ value: tier, label: SETTING_QUALITY_TIER_NAMES[tier] })),
    ],
  },
  defaultValue: AUTO,
  apply: choiceApply((value: string): void => {
    setPreferredTier(QUALITY_TIERS.find((tier) => tier === value) ?? null);
  }),
};
